/**
 * UI-атомы дизайн-системы КОММА (порт из support, очищенный от тикет-домена).
 *
 * Здесь только доменно-нейтральные примитивы, опирающиеся на классы global.css.
 * Все стили — в CSS, компоненты лишь раскладывают разметку и пробрасывают props.
 */

import { forwardRef, type CSSProperties, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

/* ---------- helpers ---------- */
// eslint-disable-next-line react-refresh/only-export-components
export function initials(name: string | undefined | null): string {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
}

/* ---------- Avatar ---------- */
export function Avatar({
  name,
  size = 36,
  color = 'var(--primary)',
}: {
  name?: string | null;
  size?: number;
  color?: string;
}) {
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, fontSize: size * 0.38, background: color, color: '#fff' }}
    >
      {initials(name) || '?'}
    </span>
  );
}

/* ---------- Button ---------- */
type ButtonVariant =
  | 'primary'
  | 'ai'
  | 'tonal'
  | 'outline'
  | 'text'
  | 'danger'
  | 'danger-ghost'
  | 'success';

export function Button({
  variant = 'primary',
  size,
  block,
  icon,
  children,
  className = '',
  ...rest
}: {
  variant?: ButtonVariant;
  size?: 'sm';
  block?: boolean;
  icon?: IconName;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const cls = [
    'btn',
    `btn-${variant}`,
    size === 'sm' ? 'btn-sm' : '',
    block ? 'btn-block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button className={cls} {...rest}>
      {icon && <Icon name={icon} className="ic" />}
      {children}
    </button>
  );
}

/* ---------- IconButton ---------- */
export const IconButton = forwardRef<
  HTMLButtonElement,
  {
    icon: IconName;
    tip?: string;
    small?: boolean;
    ghost?: boolean;
    danger?: boolean;
    className?: string;
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(function IconButton({ icon, tip, small, ghost, danger, className = '', ...rest }, ref) {
  const cls = [
    'iconbtn',
    small ? 'sm' : '',
    ghost ? 'ghost' : '',
    danger ? 'danger' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button ref={ref} className={cls} data-tip={tip} aria-label={tip} {...rest}>
      <Icon name={icon} />
    </button>
  );
});

/* ---------- Badge (generic) ---------- */
export function Badge({
  tone = 'b-neutral',
  icon,
  children,
}: {
  tone?: string;
  icon?: IconName;
  children: ReactNode;
}) {
  return (
    <span className={'badge ' + tone}>
      {icon && <Icon name={icon} className="ic" />}
      {children}
    </span>
  );
}

/* ---------- Chip (generic) ---------- */
export function Chip({
  children,
  style,
  className = '',
}: {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <span className={'chip ' + className} style={style}>
      {children}
    </span>
  );
}

/* ---------- Empty ---------- */
export function Empty({
  icon = 'Inbox',
  title,
  text,
}: {
  icon?: IconName;
  title: string;
  text?: string;
}) {
  return (
    <div className="empty">
      <span className="empty__ic">
        <Icon name={icon} />
      </span>
      <div className="col center" style={{ gap: 3 }}>
        <div style={{ fontWeight: 800, fontSize: 15.5 }}>{title}</div>
        {text && (
          <div className="muted" style={{ fontSize: 13.5 }}>
            {text}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- FormError (инлайн-ошибка валидации/сохранения) ---------- */
export function FormError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <div
      className="row center gap8"
      role="alert"
      style={{
        padding: '9px 12px',
        borderRadius: 'var(--r-sm)',
        background: 'var(--danger-bg)',
        color: 'var(--danger-fg)',
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.4,
      }}
    >
      <Icon name="Alert" style={{ width: 16, height: 16, flex: 'none' }} />
      <span>{message}</span>
    </div>
  );
}

/* ---------- Spinner / loading / error блоки ---------- */
export function Spinner({ style }: { style?: CSSProperties }) {
  return <span className="spin" style={{ width: 28, height: 28, ...style }} />;
}

export function LoadingBlock({ label = 'Загрузка…' }: { label?: string }) {
  return (
    <div className="col center jcenter" style={{ padding: '56px 20px', gap: 14 }}>
      <Spinner />
      <span className="muted" style={{ fontSize: 13.5, fontWeight: 600 }}>
        {label}
      </span>
    </div>
  );
}

export function ErrorBlock({
  title = 'Не удалось загрузить данные',
  text = 'Сервис временно недоступен. Попробуйте обновить страницу.',
  onRetry,
}: {
  title?: string;
  text?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="empty">
      <span className="empty__ic" style={{ color: 'var(--danger-fg)' }}>
        <Icon name="Alert" />
      </span>
      <div className="col center" style={{ gap: 6 }}>
        <div style={{ fontWeight: 800, fontSize: 15.5 }}>{title}</div>
        <div className="muted" style={{ fontSize: 13.5 }}>
          {text}
        </div>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            icon="Activity"
            onClick={onRetry}
            style={{ marginTop: 8 }}
          >
            Повторить
          </Button>
        )}
      </div>
    </div>
  );
}
